'use client';

import { EventCardFragment } from '@/__generated__/graphql';
import { parseProgramCategory, parseVenue } from '@/lib/parsers/categories';
import { parseEventTimeFragment, useColorScheme } from '@/lib/parsers/common';
import { useCountdown } from '@/lib/utils/useCountdown';
import { useBreakpointMax } from '@/theme';
import { excludeEmptyTypedElements, firstNonNullable } from '@liquorice/utils';
import { Dialog } from '@mui/material';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import IconButton from '../IconButton';
import Box, { BoxProps } from '../ui/Box';
import Btn, { BtnProps } from '../ui/Btn';
import Flex from '../ui/Flex';
import Txt from '../ui/Txt';
import PageTicketButton from './PageTicketButton';

type PageTicketProps = BoxProps<
  'div',
  {
    data: EventCardFragment;
    view?: 'default' | 'compact';
    TicketBtnProps?: BtnProps;
  }
>;

export default function PageTicket({
  children,
  TicketBtnProps,
  view = 'default',
  data,
  ...props
}: PageTicketProps) {
  const [open, setOpen] = useState(false);
  const t = useTranslations();
  const sm = useBreakpointMax('sm');

  const {
    entryTitle,
    venueCategory,
    programCategory,
    eventId,
    eventTime,
    status,
    onSale,
    eventReleaseDate,
  } = data;

  const program = parseProgramCategory(
    firstNonNullable(excludeEmptyTypedElements(programCategory))
  );

  const { colorScheme: programColorScheme } = program ?? {};
  const { oppositeScheme } = useColorScheme(programColorScheme, 'pink');

  const { countdownActive, timeLeft } = useCountdown(eventReleaseDate);

  const eventTimes = parseEventTimeFragment(eventTime);
  const venue = parseVenue(
    excludeEmptyTypedElements(venueCategory ?? []) as Parameters<typeof parseVenue>[0]
  );

  const isExpired = status === 'expired';
  const isDisabled = isExpired || eventTimes?.length <= 0 || countdownActive;
  // const isCountingDown = countdownActive && !isExpired;

  const isCompact = view === 'compact';

  const BtnProps: BtnProps = {
    children: countdownActive
      ? isCompact
        ? timeLeft
        : `${t('actions.availableIn', { time: timeLeft })}`
      : isExpired
      ? t('entryIndex.thisIsAnArchived', { type: t('entryIndex.event') })
      : t('actions.tickets'),
    color: isExpired ? 'error' : oppositeScheme,
    disabled: isDisabled,
    size: 'large',
    uppercase: true,
    weight: 'medium',
    startIcon: !isExpired ? (countdownActive ? 'clock' : 'ticket') : undefined,
    fullWidth: true,
    onClick: () => setOpen(true),
    ...TicketBtnProps,
  };

  if (!onSale) return null;

  return (
    <>
      <Box {...props}>
        <Btn {...BtnProps} />
      </Box>
      <Dialog
        fullScreen={sm}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        PaperProps={{ sx: { width: '100%' } }}
        open={open}>
        <Flex
          direction="row"
          justifyContent="between"
          cx={{ pX: 'md', pY: 'xs', rowGap: 'md', columnGap: 'md' }}>
          <Txt variant="large" color="black" weight="bold">
            {t('actions.selectPerformance', { type: entryTitle })}
          </Txt>
          <IconButton icon="close" onClick={() => setOpen(false)} />
        </Flex>
        <Flex rowGap="xs" cx={{ pX: 'md', pB: 'xs' }}>
          {children ||
            eventTimes?.map((time) => {
              return (
                <PageTicketButton
                  key={time?.eventTimeId}
                  eventId={eventId}
                  time={time}
                  venueCategory={venue}
                />
              );
            })}
        </Flex>
      </Dialog>
    </>
  );
}
